import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  CreditCard,
  RefreshCw,
  Search,
  Link as LinkIcon,
  Copy,
  Mail,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Loader2,
  ExternalLink,
  Filter,
} from "lucide-react";
import { API } from "../../utils/api";

const PHASES = [
  { id: "all", label: "Todos", color: "gray" },
  { id: "not_started", label: "No iniciado", color: "red" },
  { id: "onboarding_pending", label: "Onboarding pendiente", color: "yellow" },
  { id: "under_review", label: "En revisión", color: "orange" },
  { id: "active", label: "Activo", color: "green" },
  { id: "restricted", label: "Restringido", color: "zinc" },
];

const PHASE_STYLES = {
  not_started: { bg: "bg-red-500/15", text: "text-red-300", border: "border-red-500/30", dot: "bg-red-400", Icon: XCircle },
  onboarding_pending: { bg: "bg-yellow-500/15", text: "text-yellow-300", border: "border-yellow-500/30", dot: "bg-yellow-400", Icon: Clock },
  under_review: { bg: "bg-orange-500/15", text: "text-orange-300", border: "border-orange-500/30", dot: "bg-orange-400", Icon: AlertTriangle },
  active: { bg: "bg-green-500/15", text: "text-green-300", border: "border-green-500/30", dot: "bg-green-400", Icon: CheckCircle2 },
  restricted: { bg: "bg-zinc-500/15", text: "text-zinc-300", border: "border-zinc-500/30", dot: "bg-zinc-400", Icon: XCircle },
};

function PhaseBadge({ phase, label }) {
  const style = PHASE_STYLES[phase] || PHASE_STYLES.not_started;
  const Icon = style.Icon;
  return (
    <span
      data-testid={`stripe-phase-badge-${phase}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

export default function StripeConnectAdmin() {
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [query, setQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [rowBusy, setRowBusy] = useState({}); // { [userId]: "refresh"|"link"|"email" }
  const [linkDialog, setLinkDialog] = useState(null); // { user, onboarding_url, email_sent }
  const [detailUser, setDetailUser] = useState(null);

  const load = useCallback(
    async (opts = {}) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (opts.refresh) params.set("refresh", "true");
        if (query.trim()) params.set("q", query.trim());
        // phase filter is handled client-side to keep counters coherent
        const res = await axios.get(`${API}/admin/stripe/status?${params.toString()}`);
        setRows(res.data.users || []);
        setCounters(res.data.counters || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Error cargando estado de Stripe");
      } finally {
        setLoading(false);
      }
    },
    [query]
  );

  useEffect(() => {
    load();
  }, []);

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      await load({ refresh: true });
      toast.success("Estado sincronizado con Stripe");
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleRefreshRow = async (u) => {
    setRowBusy((s) => ({ ...s, [u.id]: "refresh" }));
    try {
      const res = await axios.post(`${API}/admin/stripe/users/${u.id}/refresh`);
      setRows((prev) => prev.map((r) => (r.id === u.id ? { ...r, ...res.data } : r)));
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al refrescar");
    } finally {
      setRowBusy((s) => {
        const { [u.id]: _, ...rest } = s;
        return rest;
      });
    }
  };

  const handleGenerateLink = async (u, sendEmail = false) => {
    setRowBusy((s) => ({ ...s, [u.id]: sendEmail ? "email" : "link" }));
    try {
      const res = await axios.post(`${API}/admin/stripe/users/${u.id}/onboarding-link`, {
        send_email: sendEmail,
        origin_url: window.location.origin,
      });
      if (sendEmail) {
        if (res.data.email_sent) toast.success(`Email enviado a ${u.email}`);
        else toast.warning("No se pudo enviar el email (revisa Resend)");
      }
      setLinkDialog({ user: u, ...res.data, initialSent: sendEmail });
      // Actualizar la fila si acabamos de crear la cuenta
      setRows((prev) => prev.map((r) => (r.id === u.id && !r.account_id ? { ...r, account_id: res.data.account_id, has_account: true, phase: "onboarding_pending", phase_label: "Onboarding pendiente" } : r)));
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      // Extraer mensaje útil incluso cuando el proxy devuelve HTML (Cloudflare 502, etc.)
      let msg = "Error al generar el link";
      if (typeof detail === "string" && detail.length < 300 && !detail.includes("<")) {
        msg = detail;
      } else if (status === 502) {
        msg = "Stripe no responde. Revisa que la API key esté vigente.";
      } else if (status === 503) {
        msg = "Stripe Connect no está configurado en el servidor.";
      } else if (status === 404) {
        msg = "Usuario no encontrado.";
      }
      toast.error(msg);
    } finally {
      setRowBusy((s) => {
        const { [u.id]: _, ...rest } = s;
        return rest;
      });
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const filtered = useMemo(() => {
    if (phaseFilter === "all") return rows;
    return rows.filter((r) => r.phase === phaseFilter);
  }, [rows, phaseFilter]);

  return (
    <div className="space-y-6" data-testid="admin-stripe-tab">
      {/* Header actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-400" />
            Stripe Connect · Estado por usuario
          </h2>
          <p className="text-gray-400 text-sm">Monitoriza el onboarding de pagos de cada anfitrión.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              data-testid="stripe-search-input"
              placeholder="Buscar por email, usuario…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-9 bg-[#15121e] border-white/10 text-white"
            />
          </div>
          <Button
            data-testid="stripe-refresh-all-btn"
            onClick={handleRefreshAll}
            disabled={refreshingAll || loading}
            className="bg-violet-500 hover:bg-violet-600 text-white"
          >
            {refreshingAll ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sincronizar con Stripe
          </Button>
        </div>
      </div>

      {/* Counters / phase filter chips */}
      <div className="flex flex-wrap gap-2">
        {PHASES.map((p) => {
          const count = p.id === "all" ? rows.length : counters?.[p.id] ?? 0;
          const active = phaseFilter === p.id;
          const style = p.id === "all" ? null : PHASE_STYLES[p.id];
          return (
            <button
              key={p.id}
              data-testid={`stripe-filter-${p.id}`}
              onClick={() => setPhaseFilter(p.id)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-all ${
                active
                  ? "bg-violet-500/20 border-violet-500 text-white"
                  : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
              }`}
            >
              {style && <span className={`w-2 h-2 rounded-full ${style.dot}`} />}
              {p.id === "all" && <Filter className="w-3.5 h-3.5" />}
              <span>{p.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${active ? "bg-white/20" : "bg-white/10"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr className="text-left text-xs uppercase text-gray-400">
              <th className="px-4 py-3">Usuario</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Fase</th>
              <th className="px-4 py-3">Cobros</th>
              <th className="px-4 py-3">Payouts</th>
              <th className="px-4 py-3">Requisitos pendientes</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                  Sin usuarios en esta fase.
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((u) => {
                const busy = rowBusy[u.id];
                const pendCount = (u.requirements_currently_due || []).length;
                return (
                  <tr
                    key={u.id}
                    className="hover:bg-white/5 transition-colors"
                    data-testid={`stripe-row-${u.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-white text-sm font-medium">
                        {u.name || u.username || "—"}
                      </div>
                      <div className="text-gray-500 text-xs">@{u.username || "sin-usuario"}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-sm">{u.email}</td>
                    <td className="px-4 py-3">
                      <PhaseBadge phase={u.phase} label={u.phase_label} />
                    </td>
                    <td className="px-4 py-3">
                      {u.charges_enabled ? (
                        <span className="text-green-400 text-xs font-medium">Habilitado</span>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.payouts_enabled ? (
                        <span className="text-green-400 text-xs font-medium">Habilitado</span>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.disabled_reason ? (
                        <span className="text-red-400 text-xs" title={u.disabled_reason}>
                          {u.disabled_reason}
                        </span>
                      ) : pendCount > 0 ? (
                        <span className="text-yellow-400 text-xs">{pendCount} campo(s)</span>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`stripe-detail-${u.id}`}
                          onClick={() => setDetailUser(u)}
                          title="Ver detalles"
                          className="p-1.5 rounded hover:bg-white/10 text-gray-300"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`stripe-refresh-${u.id}`}
                          onClick={() => handleRefreshRow(u)}
                          disabled={!!busy}
                          title="Refrescar estado"
                          className="p-1.5 rounded hover:bg-white/10 text-gray-300 disabled:opacity-40"
                        >
                          {busy === "refresh" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          data-testid={`stripe-link-${u.id}`}
                          onClick={() => handleGenerateLink(u, false)}
                          disabled={!!busy || u.phase === "active"}
                          title="Copiar link de onboarding"
                          className="p-1.5 rounded hover:bg-white/10 text-violet-300 disabled:opacity-40"
                        >
                          {busy === "link" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <LinkIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          data-testid={`stripe-email-${u.id}`}
                          onClick={() => handleGenerateLink(u, true)}
                          disabled={!!busy || u.phase === "active" || !u.email}
                          title="Enviar link por email"
                          className="p-1.5 rounded hover:bg-white/10 text-blue-300 disabled:opacity-40"
                        >
                          {busy === "email" ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Onboarding link result dialog */}
      <Dialog open={!!linkDialog} onOpenChange={(open) => !open && setLinkDialog(null)}>
        <DialogContent className="bg-[#231f30] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-violet-400" />
              Link de onboarding de Stripe
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Para {linkDialog?.user?.email}. Este link expira en unos minutos y sólo se puede usar una vez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-[#15121e] border border-white/10 break-all text-xs text-gray-300">
              {linkDialog?.onboarding_url}
            </div>
            {linkDialog?.email_sent && (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 className="w-4 h-4" /> Email enviado a {linkDialog?.user?.email}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="stripe-link-copy-btn"
                variant="outline"
                onClick={() => copyToClipboard(linkDialog?.onboarding_url || "")}
                className="border-white/10 text-gray-200 hover:bg-white/5"
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar link
              </Button>
              {!linkDialog?.email_sent && (
                <Button
                  data-testid="stripe-link-send-email-btn"
                  onClick={() => handleGenerateLink(linkDialog.user, true)}
                  className="bg-violet-500 hover:bg-violet-600 text-white"
                >
                  <Mail className="w-4 h-4 mr-2" /> Enviar por email
                </Button>
              )}
              <a
                href={linkDialog?.onboarding_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-200 text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Abrir en nueva pestaña
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailUser} onOpenChange={(open) => !open && setDetailUser(null)}>
        <DialogContent className="bg-[#231f30] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Detalles de Stripe Connect</DialogTitle>
            <DialogDescription className="text-gray-400">
              {detailUser?.email}
            </DialogDescription>
          </DialogHeader>
          {detailUser && (
            <div className="space-y-3 text-sm">
              <Row label="Fase">
                <PhaseBadge phase={detailUser.phase} label={detailUser.phase_label} />
              </Row>
              <Row label="Account ID">
                <span className="font-mono text-xs text-gray-300 break-all">
                  {detailUser.account_id || "—"}
                </span>
                {detailUser.account_id && (
                  <button
                    onClick={() => copyToClipboard(detailUser.account_id)}
                    className="ml-2 text-violet-300 hover:text-violet-200"
                    title="Copiar ID"
                  >
                    <Copy className="w-3.5 h-3.5 inline" />
                  </button>
                )}
              </Row>
              <Row label="Cobros habilitados">
                <BoolPill value={detailUser.charges_enabled} />
              </Row>
              <Row label="Payouts habilitados">
                <BoolPill value={detailUser.payouts_enabled} />
              </Row>
              <Row label="Datos enviados">
                <BoolPill value={detailUser.details_submitted} />
              </Row>
              {detailUser.disabled_reason && (
                <Row label="Motivo de restricción">
                  <span className="text-red-300">{detailUser.disabled_reason}</span>
                </Row>
              )}
              {detailUser.requirements_currently_due?.length > 0 && (
                <div>
                  <div className="text-gray-400 mb-1">Requisitos pendientes en Stripe:</div>
                  <ul className="list-disc pl-6 text-yellow-300 space-y-1">
                    {detailUser.requirements_currently_due.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDetailUser(null)}
              className="border-white/10 text-gray-200 hover:bg-white/5"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1 border-b border-white/5">
      <span className="text-gray-400">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

function BoolPill({ value }) {
  return value ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/15 text-green-300 border border-green-500/30">
      <CheckCircle2 className="w-3 h-3" /> Sí
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white/5 text-gray-400 border border-white/10">
      No
    </span>
  );
}
